<template>
  <div
    class="modal modal-open items-start !animate-none overflow-y-auto"
  >
    <div
      class="absolute top-0 bottom-0 right-0 left-0"
      @click.prevent="$emit('close')"
    />
    <div class="modal-box pt-4 pb-6 px-6 mt-20 w-full max-w-xl">
      <div class="flex justify-between items-center border-b pb-2 mb-2 font-medium">
        <span class="modal-title">
          {{ t('condition') }} - {{ (defaultField ? (defaultField.title || item.title || item.name) : item.name) || buildDefaultName(item) }}
        </span>
        <a
          href="#"
          class="text-xl modal-close-button"
          @click.prevent="$emit('close')"
        >&times;</a>
      </div>
      <div>
        <div
          v-if="!withConditions"
          class="bg-base-300 rounded-xl py-2 px-3 text-center"
        >
          <a
            href="https://www.docuseal.com/pricing"
            target="_blank"
            class="link"
          >{{ t('available_in_pro') }}</a>
        </div>
        <form @submit.prevent="validateSaveAndClose">
          <div class="my-4">
            <div
              v-for="(condition, cindex) in conditions"
              :key="cindex"
              class="space-y-4 relative"
            >
              <div
                v-if="cindex > 0"
                class="divider -mb-2 mx-1"
              >
                <button
                  class="btn btn-xs btn-primary w-24"
                  @click.prevent="condition.operation === 'or' ? delete condition.operation : condition.operation = 'or'"
                >
                  {{ condition.operation === 'or' ? t('or') : t('and') }}
                </button>
              </div>
              <div
                v-if="conditions.length > 1"
                class="flex justify-between mx-1"
              >
                <label class="text-sm">
                  {{ t('condition') }} {{ cindex + 1 }}
                </label>
                <a
                  href="#"
                  class="link text-sm"
                  @click.prevent="conditions.splice(cindex, 1)"
                > {{ t('remove') }}</a>
              </div>
              <select
                class="select select-bordered select-sm w-full bg-white h-11 pl-4 text-base font-normal"
                :class="{ 'text-gray-300': !condition.field_uuid }"
                required
                @change="[
                  condition.field_uuid = $event.target.value,
                  delete condition.value,
                  (conditionActions(condition).includes(condition.action) ? '' : condition.action = conditionActions(condition)[0])
                ]"
              >
                <option
                  value=""
                  disabled
                  :selected="!condition.field_uuid"
                >
                  {{ t('select_field_') }}
                </option>
                <option
                  v-for="f in fields"
                  :key="f.uuid"
                  :value="f.uuid"
                  class="text-base-content"
                  :selected="condition.field_uuid === f.uuid"
                >
                  {{ f.name || buildDefaultName(f) }}
                </option>
              </select>
              <select
                v-model="condition.action"
                class="select select-bordered select-sm w-full h-11 pl-4 text-base font-normal"
                :class="{ 'bg-white': condition.field_uuid, 'bg-base-300': !condition.field_uuid }"
                :required="condition.field_uuid"
              >
                <option
                  v-for="action in conditionActions(condition)"
                  :key="action"
                  :value="action"
                >
                  {{ t(action) }}
                </option>
              </select>
              <select
                v-if="['radio', 'select', 'multiple'].includes(conditionField(condition)?.type) && conditionField(condition)?.options"
                class="select select-bordered select-sm w-full bg-white h-11 pl-4 text-base font-normal"
                :class="{ 'text-gray-300': !condition.value }"
                required
                @change="condition.value = $event.target.value"
              >
                <option
                  value=""
                  disabled
                  selected
                >
                  {{ t('select_value_') }}
                </option>
                <option
                  v-for="(option, index) in conditionField(condition).options"
                  :key="option.uuid"
                  :value="option.uuid"
                  :selected="condition.value === option.uuid"
                  class="text-base-content"
                >
                  {{ option.value || `${t('option')} ${index + 1}` }}
                </option>
              </select>
              <input
                v-else-if="conditionField(condition)?.type === 'number' && ['equal', 'not_equal', 'greater_than', 'less_than'].includes(condition.action)"
                v-model="condition.value"
                type="number"
                step="any"
                class="input input-bordered input-sm w-full bg-white h-11 pl-4 text-base font-normal"
                :class="{ 'text-gray-300': !condition.value }"
                :placeholder="t('type_value')"
                required
              >
            </div>
          </div>
          <a
            href="#"
            class="inline float-right link text-right mb-3 px-2"
            @click.prevent="conditions.push({})"
          > + {{ t('add_condition') }}</a>
          <div
            v-if="item.submitter_uuid"
            class="clear-both border-t border-base-300 pt-4 mt-12 space-y-2"
          >
            <label class="text-sm font-medium">Advanced conditional rule</label>
            <select
              v-model="selectedRule"
              class="select select-bordered select-sm w-full bg-white h-11 pl-4 text-base font-normal"
            >
              <option value="visible">
                Visible when
              </option>
              <option value="required">
                Required when
              </option>
              <option value="readonly">
                Read-only when
              </option>
            </select>
            <textarea
              v-model="rules[selectedRule]"
              class="base-textarea !rounded-xl font-mono w-full"
              placeholder="{{Field}} == 'value'"
            />
            <div class="flex flex-wrap gap-1">
              <button
                v-for="field in fields"
                :key="`rule-${field.uuid}`"
                class="btn btn-xs btn-outline normal-case"
                @click.prevent="rules[selectedRule] += `{{${field.name || buildDefaultName(field)}}}`"
              >
                {{ field.name || buildDefaultName(field) }}
              </button>
            </div>
            <p class="text-xs text-base-content/60">
              Use comparisons, and/or/not, parentheses, and same-template field references.
            </p>
          </div>
          <button class="base-button w-full mt-2 modal-save-button">
            {{ t('save') }}
          </button>
        </form>
        <div
          v-if="item.conditions?.[0]?.field_uuid || Object.values(item.conditional_rules || {}).some(Boolean)"
          class="text-center w-full mt-4"
        >
          <button
            class="link"
            @click="removeConditions"
          >
            {{ t('remove_condition') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TemplateEvaluator } from '../shared/field_expressions'

export default {
  name: 'ConditionModal',
  inject: ['t', 'template', 'withConditions'],
  props: {
    item: {
      type: Object,
      required: true
    },
    defaultField: {
      type: Object,
      required: false,
      default: null
    },
    buildDefaultName: {
      type: Function,
      required: true
    },
    excludeFieldUuids: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['close', 'save'],
  data () {
    return {
      conditions: this.item.conditions?.[0] ? JSON.parse(JSON.stringify(this.item.conditions)) : [{}],
      selectedRule: 'visible',
      rules: Object.fromEntries(['visible', 'required', 'readonly'].map((key) => [key, this.humanizeRule(this.item.conditional_rules?.[key] || '')]))
    }
  },
  computed: {
    excludeTypes () {
      return ['heading', 'strikethrough']
    },
    fields () {
      if (this.item.submitter_uuid) {
        return this.template.fields.reduce((acc, f) => {
          if (f !== this.item && !this.excludeTypes.includes(f.type) && !this.excludeFieldUuids.includes(f.uuid) && (!f.conditions?.length || !f.conditions.find((c) => c.field_uuid === this.item.uuid))) {
            acc.push(f)
          }

          return acc
        }, [])
      } else {
        return this.template.fields.filter((f) => !this.excludeFieldUuids.includes(f.uuid))
      }
    }
  },
  created () {
    this.item.conditions ||= []
  },
  methods: {
    conditionField (condition) {
      return this.fields.find((f) => f.uuid === condition.field_uuid)
    },
    conditionActions (condition) {
      return this.fieldActions(this.conditionField(condition))
    },
    fieldActions (field) {
      const actions = []

      if (!field) {
        return actions
      }

      if (field.type === 'checkbox') {
        actions.push('checked', 'unchecked')
      } else if (['radio', 'select'].includes(field.type)) {
        actions.push('equal', 'not_equal')
      } else if (['multiple'].includes(field.type)) {
        actions.push('contains', 'does_not_contain')
      } else if (field.type === 'number') {
        actions.push('not_empty', 'empty', 'equal', 'not_equal', 'greater_than', 'less_than')
      } else {
        actions.push('not_empty', 'empty')
      }

      return actions
    },
    validateSaveAndClose () {
      if (!this.withConditions) {
        return alert(this.t('available_only_in_pro'))
      }

      const previousConditions = this.item.conditions
      const previousRules = this.item.conditional_rules

      if (this.conditions.find((f) => f.field_uuid)) {
        this.item.conditions = this.conditions
      } else {
        delete this.item.conditions
      }

      this.item.conditional_rules = Object.fromEntries(Object.entries(this.rules)
        .map(([key, value]) => [key, this.normalizeRule(value)])
        .filter(([, value]) => value))
      if (!Object.keys(this.item.conditional_rules).length) delete this.item.conditional_rules

      try {
        const evaluator = new TemplateEvaluator(this.template.fields, {})
        evaluator.validate()
      } catch (_) {
        this.item.conditions = previousConditions
        this.item.conditional_rules = previousRules
        return alert('Invalid conditional expression or circular field reference')
      }

      this.$emit('save')
      this.$emit('close')
    },
    humanizeRule (text) {
      return text.replace(/{{(.*?)}}/g, (match, uuid) => {
        const field = this.template.fields.find((candidate) => candidate.uuid === uuid)
        return field ? `{{${field.name || this.buildDefaultName(field)}}}` : '{{FIELD NOT FOUND}}'
      })
    },
    normalizeRule (text) {
      return text.replace(/{{(.*?)}}/g, (match, name) => {
        const field = this.template.fields.find((candidate) => (candidate.name || this.buildDefaultName(candidate)).trim() === name.trim())
        return field ? `{{${field.uuid}}}` : '{{FIELD NOT FOUND}}'
      }).trim()
    },
    removeConditions () {
      this.conditions = []
      this.rules = { visible: '', required: '', readonly: '' }
      delete this.item.conditions
      delete this.item.conditional_rules
      this.validateSaveAndClose()
    }
  }
}
</script>
